// src/components/seo.js
import React from "react";
import { Helmet } from "react-helmet";

// ---- site defaults
const SITE_NAME = "TarotFate";
const DEFAULT_IMAGE = "https://tarotfate.com/icons/og-cover.jpg";
const DEFAULT_ROBOTS =
  "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

// ---- site-wide mega keywords (ALWAYS included)
const MEGA_KEYWORDS = `
tarot, tarot reading, tarot card meanings, free tarot, online tarot reading, accurate tarot, instant tarot,
yes or no tarot, one card reading, three card spread, five card spread, celtic cross spread, past present future tarot,
upright meanings, reversed meanings, major arcana, minor arcana, how to read tarot, beginner tarot guide,
advanced tarot guide, tarot tips and spreads, mindful tarot, reflective tarot, emotion guided tarot,
TarotFate, contact TarotFate, donate TarotFate, creator story, about TarotFate,
tarot in English, tarot en Español, tarot em Português, tarot en Français, tarot auf Deutsch, tarocchi in Italiano, tarot Türkçe,
تاروت عربي, टैरो हिंदी, ٹروٹ اردو, tarot বাংলা, tarot 日本語, tarot 한국어, tarot Bahasa Indonesia, tarot Tagalog,
tarot Tiếng Việt, tarot ไทย, USA tarot, UK tarot, Canada tarot, Australia tarot, India tarot,
spiritual insight, emotional insight, introspection, gentle guidance
`.replace(/\s+/g, " ").trim();

// helper to merge + dedupe keywords
function mergeKeywords(pageKeywords) {
  const split = (s) =>
    (s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const all = [...split(pageKeywords), ...split(MEGA_KEYWORDS)];
  // simple de-dupe (case-insensitive)
  const seen = new Set();
  const deduped = [];
  for (const k of all) {
    const key = k.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(k);
    }
  }
  return deduped.join(", ");
}

export default function Seo({
  title,
  description,
  url,
  image = DEFAULT_IMAGE,
  schema,
  keywords // optional page-specific keywords; mega list is always appended
}) {
  const keywordContent = mergeKeywords(keywords);

  return (
    <Helmet>
      <title>{title}</title>

      {/* Basics */}
      {description && <meta name="description" content={description} />}
      {keywordContent && <meta name="keywords" content={keywordContent} />}
      <meta name="robots" content={DEFAULT_ROBOTS} />
      {url && <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={image || DEFAULT_IMAGE} />
      <meta property="og:image:alt" content={title} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image || DEFAULT_IMAGE} />

      {/* JSON-LD */}
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </Helmet>
  );
}
